import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Table,
  message,
  notification,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  getCustomers,
  UpdateFlag,
  getChildrenList,
  submitTripodData,
  submitSevenPodData,
  getUsersChildrenLists,
} from "../../../../utils/API";
import { RiCloseLine } from "react-icons/ri";
import { MdCheckBox } from "react-icons/md";

const { Option } = Select;

interface Customer {
  _id: any;
  firstName: string;
  lastName: string;
  username: string;
  userId: any;
  email: string;
  mobileNo: string;
}

const Tripod: React.FC = () => {
  const [customerData, setCustomerData] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [kycFilter, setKycFilter] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalUser, setModalUser] = useState<Customer | null>(null);
  const [dropdownCount, setDropdownCount] = useState<number | null>(null);
  const [leftChildren, setLeftChildren] = useState<Customer[][]>([]);
  const [rightChildren, setRightChildren] = useState<Customer[][]>([]);
  const [mainId, setMainId] = useState<string | null>(null);
  const [dropdownIds, setDropdownIds] = useState<{
    [key: number]: {
      leftId: string | null;
      rightId: string | null;
      leftChildren?: Customer[];
      rightChildren?: Customer[];
    };
  }>({});

  const [actionType, setActionType] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tripodLoading, setTripodLoading] = useState(false);
  const [sevenpodLoading, setSevenpodLoading] = useState(false);

  const fetchCustomerData = (page: any, searchText: any) => {
    const customerId = localStorage.getItem("customerId") || "";
    const customuserId = customerId.replace(/"/g, "");

    setLoading(true);
    getUsersChildrenLists(customuserId, page, searchText)
      .then((response: any) => {
        setCustomerData(response.data.children.reverse());
        setTotalPages(response.data.totalPages);
        setCurrentPage(response.data.currentPage);
        setLoading(false);
      })
      .catch((error: any) => {
        console.error("Error occurred:", error);
        message.error("Failed to fetch customer data. Please try again.");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchCustomerData(currentPage, searchText);
  }, [currentPage, searchText]);

  const openModal = (
    user: Customer,
    dropdownCount: number | null,
    type: string
  ) => {
    user.userId = user._id || user.userId;
    console.log(user);
    setModalUser(user);
    setIsModalOpen(true);
    setDropdownCount(dropdownCount);
    setMainId(user.userId);
    fetchChildrenList(user.userId);
    initializeDropdownIds(dropdownCount);
    setActionType(type);
  };

  const handleSearch = (value: any) => {
    setSearchText(value);
  };

  const initializeDropdownIds = (count: number | null) => {
    if (count === null) return;

    let ids: {
      [key: number]: { leftId: string | null; rightId: string | null };
    } = {};
    for (let i = 0; i < count; i++) {
      ids[i] = { leftId: null, rightId: null };
    }
    setDropdownIds(ids);
  };

  const fetchChildrenList = (userId: any) => {
    getChildrenList(userId)
      .then((response: any) => {
        const { leftchildren, rightchildren } = response.data;
        setLeftChildren(leftchildren.flat());
        setRightChildren(rightchildren.flat());
      })
      .catch((error: any) => {
        console.error("Error occurred:", error);
        message.error("Failed to fetch children list. Please try again.");
      });
  };



  const handleLeftDropdownChange = (value: string, dropdownIndex: number) => {
    setDropdownIds((prevIds) => ({
      ...prevIds,
      [dropdownIndex]: {
        ...prevIds[dropdownIndex],
        leftId: value,
      },
    }));
    fetchChildrenForDropdown(value, dropdownIndex, 'left');
  };

  const handleRightDropdownChange = (value: string, dropdownIndex: number) => {
    setDropdownIds((prevIds) => ({
      ...prevIds,
      [dropdownIndex]: {
        ...prevIds[dropdownIndex],
        rightId: value,
      },
    }));
    fetchChildrenForDropdown(value, dropdownIndex, 'right');
  };

  const generateDropdowns = () => {
    if (dropdownCount === null) {
      return null;
    }

    const dropdowns = [];
    for (let i = 0; i < dropdownCount; i++) {
      dropdowns.push(
        <div key={i} className="d-flex mt-5">
          <div className="w-1/2 p-1">
            <p className="mr-2 mb-1">Left User</p>
            <Select
              placeholder={`Select user `}
              showSearch
              allowClear
              optionFilterProp="label"
              filterOption={(input: any, option: any) =>
                option?.label.toLowerCase().includes(input.toLowerCase())
              }
              className="mr-2"
              size="large"
              aria-required
              style={{ width: "100%" }}
              onChange={(value) => handleLeftDropdownChange(value, i)}
              value={dropdownIds[i]?.leftId || undefined}
            >
              {leftChildren.map((child: any) => (
                <Option key={child._id} value={child._id} label={`${child.firstName} - ${child.username}`}>
                  {child.firstName} - {child.username}{" "}
                </Option>
              ))}
            </Select>
          </div>
          <div className="w-1/2 p-1">
            <p className="mr-2 mb-1">Right User</p>
            <Select
              placeholder={`Select user `}
              style={{ width: "100%" }}
              size="large"
              onChange={(value) => handleRightDropdownChange(value, i)}
              value={dropdownIds[i]?.rightId || undefined}
              showSearch
              allowClear
              optionFilterProp="label"
              filterOption={(input: any, option: any) =>
                option?.label.toLowerCase().includes(input.toLowerCase())
              }
            >
              {(dropdownIds[i]?.leftChildren || rightChildren).map((child: any) => (
                <Option key={child._id} value={child._id} label={`${child.firstName} - ${child.username}`}>
                  {child.firstName} - {child.username}{" "}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      );
    }
    return dropdowns;
  };


  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text: any, record: any) =>
        `${record.firstName} ${record.lastName}`,
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Mobile Number",
      dataIndex: "mobileNo",
      key: "mobileNo",
    },
    {
      title: "Status",
      key: "Status",
      render: (_: any, record: any) => (
        <div className="d-flex">
          <p
            className={`rounded-md ${record.tripod === 0 || record.sevenPod === 0
                ? "text-gray-500"
                : record.tripod === 1 || record.sevenPod === 1
                  ? "cursor-not-allowed text-orange-500 opacity-50"
                  : record.tripod === 2 || record.sevenPod === 2
                    ? "text-blue-500"
                    : "text-green-500"
              }`}
          >
            {record.tripod === 0 && record.sevenPod === 0
              ? "Not Applied"
              : record.tripod === 1 || record.sevenPod === 1
                ? "Pending from Admin Approval"
                : record.tripod === 2 || record.sevenPod === 2
                  ? "Approved"
                  : "Not Applied"}
          </p>

        </div>
      ),
    },

    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <div className="d-flex">
          <button
            className={`bg-orange-500 text-white px-3 py-2 rounded-md ${record.sevenPod >= 1 || record.tripod >= 1 ? "cursor-not-allowed opacity-50" : ""
              }`}
            onClick={() => openModal(record, 1, "tripod")}
            // disabled={record.sevenPod >= 1 || record.tripod >= 1 || record.subSevenpod == 1|| record.subTripod == 1}
            disabled={record.sevenPod >= 1 || record.tripod >= 1}
          >
            Tripod
          </button>

          <button
            className={`bg-orange-500 text-white px-3 py-2 rounded-md ml-2 ${record.sevenPod >= 1 || record.tripod >= 1 ? "cursor-not-allowed opacity-50" : ""
              }`}
            onClick={() => openModal(record, 3, "sevenpod")}
            // disabled={record.sevenPod >= 1 || record.tripod >= 1 || record.subSevenpod == 1|| record.subTripod == 1}
            disabled={record.sevenPod >= 1 || record.tripod >= 1}
          >
            SevenPod
          </button>
        </div>

      ),
    },
    {
      title: "Tripod / Sevenpod",
      key: "ApprovedPod",
      render: (_: any, record: any) => (
        <div className="d-flex text-center">
          {record.tripod === 1
            ? <p className="text-red-500 text-center">Tripod</p>
            : record.sevenPod === 1
              ? <p className="text-red-500 text-center">SevenPod</p>

              : <p className="text-gray-500 text-center">
                {record.tripod === 2
                  ? <p className="text-green-500 text-center">Tripod </p>
                  : record.sevenPod === 2
                    ? <p className="text-green-500 text-center">SevenPod </p>
                    : "Not Applied"
                }
              </p>
          }
        </div>
      ),
    },
  ];

  const handleModalOk = async () => {
    if (mainId !== null && dropdownIds !== null) {
      const userId = localStorage.getItem("customerId") || "";
      const submitedId = userId.replace(/"/g, "");
      let data: any = {
        mainId,
        submitedId,
      };

      let allDropdownsSelected = true;
      let uniqueSelections = new Set();

      Object.keys(dropdownIds).forEach((key: any) => {
        const { leftId, rightId } = dropdownIds[key];
        if (!leftId || !rightId) {
          allDropdownsSelected = false;
        }
        uniqueSelections.add(leftId);
        uniqueSelections.add(rightId);
      });

      if (!allDropdownsSelected) {
        message.error(`Left and Right IDs are required for ${actionType} Declaration.`);
        return;
      }

      if (uniqueSelections.size !== Object.keys(dropdownIds).length * 2) {
        message.error("Duplicate selections are not allowed.");
        return;
      }

      if (actionType === "tripod") {
        Object.keys(dropdownIds).forEach((key: any, index: any) => {
          data[`leftId`] = dropdownIds[key].leftId;
          data[`rightId`] = dropdownIds[key].rightId;
        });
      } else if (actionType === "sevenpod") {
        Object.keys(dropdownIds).forEach((key: any, index: any) => {
          data[`leftId${index + 1}`] = dropdownIds[key].leftId;
          data[`rightId${index + 1}`] = dropdownIds[key].rightId;
        });
      }

      try {
        let result;
        if (actionType === "tripod") {
          setTripodLoading(true);
          result = await submitTripodData(data).then((response: any) => {
            fetchCustomerData(currentPage, searchText);
          });
        } else if (actionType === "sevenpod") {
          setSevenpodLoading(true);
          result = await submitSevenPodData(data);
          fetchCustomerData(currentPage, searchText);
        }
        fetchCustomerData(currentPage, searchText);
        notification.success({
          message: "Data submitted successfully.",
          description: "Your data has been submitted successfully.",
          icon: <MdCheckBox style={{ color: "#52c41a" }} />,
        });
      } catch (error) {
        message.error("Failed to submit data.");
      } finally {
        setDropdownIds({});
        setIsModalOpen(false);
        setTripodLoading(false);
        setSevenpodLoading(false);
      }
    } else {
      message.error("Please select values for all dropdowns.");
    }
  };

  const handleModalCancel = () => {
    setDropdownIds({});
    setIsModalOpen(false);
  };

  const fetchChildrenForDropdown = (userId: string, dropdownIndex: number, side: 'left' | 'right') => {
    getChildrenList(userId)
      .then((response: any) => {
        const children = response.data.children; // Adjust based on actual response structure

        setDropdownIds((prevIds) => ({
          ...prevIds,
          [dropdownIndex]: {
            ...prevIds[dropdownIndex],
            [`${side}Children`]: children, // Store children separately for left and right dropdowns
          },
        }));
      })
      .catch((error: any) => {
        console.error("Error occurred:", error);
        message.error("Failed to fetch children list. Please try again.");
      });
  };

  return (
    <div className="tripod-component">
      <Row className="m-2" align="middle">
        <Col xs={24} sm={24} md={9} xl={16} xxl={16}>
          <span className="text-black text-lg font-bold">
            Tripod Declaration
          </span>
        </Col>
        <Col
          xs={24}
          sm={24}
          md={15}
          xl={8}
          xxl={8}
          className="d-flex justify-end items-center"
        >
          <Input
            placeholder="Search By Name, Username"
            prefix={<SearchOutlined className="mr-1" />}
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            size="large"
          />
        </Col>
      </Row>
      <Table
        columns={columns}
        scroll={{ x: 1000 }}
        dataSource={customerData}
        rowKey={(record) => record.userId}
        loading={loading}
        pagination={{
          current: currentPage,
          total: totalPages * 10,
          onChange: (page) => setCurrentPage(page),
        }}
      />
      <Modal
        title={`${actionType === "tripod" ? "Tripod" : "SevenPod"} - ${modalUser?.username}`}
        visible={isModalOpen}
        footer={null}
        closeIcon={
          <RiCloseLine
            className="cursor-pointer"
            onClick={handleModalCancel}
            style={{ fontSize: "20px", color: "black" }}
          />
        }
      >
        <Spin spinning={tripodLoading || sevenpodLoading}>
          {modalUser && (
            <>
              {generateDropdowns()}
              <div className="d-flex justify-end pt-10">
                <Button
                  onClick={handleModalOk}
                  className="rounded"
                  style={{
                    backgroundColor: "orange",
                    color: "white",
                    marginRight: "1rem",
                  }}
                  loading={tripodLoading || sevenpodLoading}
                >
                  {tripodLoading
                    ? "Submitting..."
                    : sevenpodLoading
                      ? "yes"
                      : "Yes"}
                </Button>
                <Button
                  onClick={handleModalCancel}
                  className="rounded bg-red-500 text-white"
                  disabled={tripodLoading || sevenpodLoading}
                >
                  No
                </Button>
              </div>
            </>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default Tripod;
